import { SpriteMap } from 'api-validations/icons'
import { isoDatetimeToDate } from 'api-validations/zod-extra'
import { path } from 'utils'
import { z } from 'zod'

import { TaskType } from '../ag-concepts'
import { OperationLevelPermission } from '../auth-claims/permissions'
import { defineApi } from '../http'
import { taskSchema } from '../pangea'
import { MeasureSystem } from '../shared'
import { applicationTypes } from '../stitch'
import { LayerPaletteKind } from './map-layer-render'
import { ReportTotalMetric, ReportType, selectedOptionsSchema } from './seasonal-task-report'

/**
 * What a cell holds. Reports carry values rather than display strings so a client can sort,
 * convert units, and export without parsing the text it was handed back apart.
 */
/**
 * Which crops a report covers. A season holds one crop or every crop it has data for; there is no
 * "some crops" in between, so a report never has to say which subset it meant.
 */
export enum CropSelectionKind {
  All = 'all',
  Single = 'single'
}

export const cropSelectionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal(CropSelectionKind.All) }),
  z.object({ kind: z.literal(CropSelectionKind.Single), crop: z.string() })
])

export type CropSelection = z.infer<typeof cropSelectionSchema>

/**
 * Reserved for the all-crops report. Crop keys are the names a grower sees ("Corn", "Soybeans"),
 * so the underscores keep this out of the space a crop could ever occupy.
 */
export const ALL_CROPS_SEGMENT = '__all__'

/**
 * The crop path segment. A URL segment can only be a string, so the selection is carried as a crop
 * name or {@link ALL_CROPS_SEGMENT} and decoded into the union a handler branches on.
 */
export const cropSegmentCodec = z.codec(z.string(), cropSelectionSchema, {
  decode: (segment): CropSelection =>
    segment === ALL_CROPS_SEGMENT
      ? { kind: CropSelectionKind.All }
      : { kind: CropSelectionKind.Single, crop: segment },
  encode: (selection) =>
    selection.kind === CropSelectionKind.All ? ALL_CROPS_SEGMENT : selection.crop
})

export enum ReportCellKind {
  Empty = 'empty',
  Text = 'text',
  Measure = 'measure',
  Timestamp = 'timestamp',
  List = 'list'
}

/**
 * What a column holds, independent of what any particular cell turned out to be. A reader sorting
 * a column needs one answer for the whole column — cells may be absent under the current filter,
 * and a column of dates is a column of dates even when none of them arrived.
 */
export enum ReportValueKind {
  Text = 'text',
  Measure = 'measure',
  Timestamp = 'timestamp'
}

export enum ReportBlockKind {
  Table = 'table',
  LayerMap = 'layerMap',
  UnavailableMap = 'unavailableMap',
  Metrics = 'metrics',
  ActivitySummary = 'activitySummary',
  Facts = 'facts',
  Group = 'group'
}

/**
 * What a record or one segment of its address names. This is identity only — what kind of thing is
 * being named — and carries no claim about how prominent it is or where it sits. A renderer uses it
 * to pick an icon and a word for it, nothing more.
 */
export enum ReportEntity {
  Client = 'client',
  Farm = 'farm',
  Field = 'field'
}

const mapLinkSchema = z.object({
  streamId: z.string(),
  applicationId: z.string(),
  layerId: z.string()
})

/**
 * No value, as distinct from a value of zero: a product that was never applied to a field has
 * neither a rate nor a rate of nothing.
 */
const emptyCellSchema = z.object({
  kind: z.literal(ReportCellKind.Empty)
})

const textCellSchema = z.object({
  kind: z.literal(ReportCellKind.Text),
  text: z.string(),
  link: mapLinkSchema.optional()
})

const measureCellSchema = z.object({
  kind: z.literal(ReportCellKind.Measure),
  value: z.number(),
  /** The unit the value is in, which a client converts from before display. */
  unit: z.string().optional(),
  link: mapLinkSchema.optional()
})

const timestampCellSchema = z.object({
  kind: z.literal(ReportCellKind.Timestamp),
  at: isoDatetimeToDate,
  link: mapLinkSchema.optional()
})

const listItemSchema = z.discriminatedUnion('kind', [
  emptyCellSchema,
  textCellSchema,
  measureCellSchema,
  timestampCellSchema
])

/** Several values in one cell, e.g. every planting date for one field, or a list of products. */
const listCellSchema = z.object({
  kind: z.literal(ReportCellKind.List),
  items: z.array(listItemSchema)
})

const reportCellSchema = z.discriminatedUnion('kind', [
  emptyCellSchema,
  textCellSchema,
  measureCellSchema,
  timestampCellSchema,
  listCellSchema
])

const reportColumnSchema = z.object({
  key: z.string(),
  label: z.string(),
  valueKind: z.enum(ReportValueKind),
  /**
   * The unit this column's measures are in, so a header can state it once rather than every row
   * repeating it.
   */
  unit: z.string().optional()
})

const reportRowBaseSchema = z.object({
  id: z.string(),
  cells: z.record(z.string(), reportCellSchema)
})

/**
 * A row, nesting arbitrarily deep. Depth follows the breakdown the reader asked for, so a row
 * with no `subRows` is a leaf rather than a level that was left out.
 */
type ReportRowNode = z.infer<typeof reportRowBaseSchema> & { subRows?: ReportRowNode[] }
const reportRowSchema: z.ZodType<ReportRowNode> = reportRowBaseSchema.extend({
  subRows: z.array(z.lazy(() => reportRowSchema)).optional()
})

const tableBlockSchema = z.object({
  kind: z.literal(ReportBlockKind.Table),
  id: z.string(),
  title: z.string(),
  icon: z.enum(SpriteMap).optional(),
  columns: z.array(reportColumnSchema),
  rows: z.array(reportRowSchema),
  /**
   * The machine whose pass this content describes, when its group gathers more than one machine's
   * passes and the content stayed one machine's own.
   */
  serialNumber: z.number().optional(),
  /**
   * The table's figures across its top-level rows, for the columns where such a figure means
   * something.
   */
  aggregate: z
    .object({
      label: z.string(),
      cells: z.record(z.string(), reportCellSchema)
    })
    .optional()
})

/**
 * One pass's render of a map layer.
 */
const layerRenderSchema = z.object({
  streamId: z.string(),
  /**
   * The colored image, relative to the api root. Its palette is already in the query, so every
   * client draws the map in the same colors.
   */
  renderPath: z.string(),
  /**
   * The box the render was drawn into, which is square in Web Mercator. A client frames the
   * satellite view around the map's renders at whatever shape its layout gives the map.
   */
  frame: z.object({
    west: z.number(),
    south: z.number(),
    east: z.number(),
    north: z.number()
  })
})

/**
 * A pass the report would draw a map of but has no render of at all, so a reader knows the map is
 * missing ground rather than reading it as all the work there was.
 */
const unavailablePassSchema = z.object({
  streamId: z.string(),
  serialNumber: z.number()
})

/**
 * One application's map layer, colored by its legend group. A pass shows one of these for every
 * application that carries the layer, so a pass with two liquid systems shows two rate maps.
 */
const layerMapBlockSchema = z.object({
  kind: z.literal(ReportBlockKind.LayerMap),
  id: z.string(),
  applicationType: applicationTypes,
  applicationId: z.string(),
  layerId: z.string(),
  layerKey: z.string(),
  /**
   * The document legend this map is colored by, which every map of the same application and layer
   * shares.
   */
  legendId: z.string(),
  /**
   * The stops of the shared legend that this map's own data holds, so the map's legend need not
   * list hybrids that only other passes planted.
   */
  stopIds: z.array(z.string()),
  /**
   * The pass's render, or every merged pass's in the order they finished, so that where two passes
   * cover the same ground the later one is drawn over the earlier.
   */
  renders: z.tuple([layerRenderSchema], layerRenderSchema),
  /**
   * The passes of the map's group that have no render of any layer, in the order they finished.
   */
  unavailable: z.array(unavailablePassSchema)
})

/**
 * In place of a group's layer maps, when none of its passes has a render of any layer.
 */
const unavailableMapBlockSchema = z.object({
  kind: z.literal(ReportBlockKind.UnavailableMap),
  id: z.string(),
  /**
   * Where the first of the passes was, for a satellite view of its ground.
   */
  location: z.object({ lat: z.number(), lon: z.number() }),
  passes: z.tuple([unavailablePassSchema], unavailablePassSchema)
})

const reconciledStopSchema = z.object({
  /**
   * The stop's label, or its value as a string when it has no label.
   */
  id: z.string(),
  /**
   * Present when the stop is a number rather than a label, for the client to format.
   */
  value: z.number().optional(),
  color: z.string()
})

/**
 * Whether the maps of one application's continuous layer read against one scale or each against its
 * own.
 */
export enum LegendScaleKind {
  /**
   * Every map of the layer shares one range and bin count, so a value is one color across the
   * report.
   */
  Shared = 'shared',
  /**
   * Each map keeps the range and bins its own pass would draw it with, for layers like elevation
   * whose values differ more between fields than within one.
   */
  PerMap = 'perMap'
}

const valueRangeSchema = z
  .object({ minimum: z.number(), maximum: z.number() })
  .refine((range) => range.maximum > range.minimum)

/**
 * The field map's legend editor offers from three to twelve continuous bins, and a report offers
 * the same.
 */
export const MIN_LEGEND_BINS = 3
export const MAX_LEGEND_BINS = 12

const binCountSchema = z.int().min(MIN_LEGEND_BINS).max(MAX_LEGEND_BINS)

/**
 * The scale a report gives one application's continuous layer. Without one, its maps share the
 * scale the report reconciles for them.
 */
export const legendScaleSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal(LegendScaleKind.Shared),
    range: valueRangeSchema,
    binCount: binCountSchema
  }),
  z.object({ kind: z.literal(LegendScaleKind.PerMap) })
])

/**
 * The legend every map of one application and layer shares, so that a value reads the same color
 * on every map in the report. When the layer is drawn per map, each map has a legend of its own,
 * still naming the application and layer it belongs to.
 */
const layerLegendSchema = z.object({
  id: z.string(),
  applicationType: applicationTypes,
  applicationId: z.string(),
  layerKey: z.string(),
  /**
   * The layer's display name from the pass that recorded it, e.g. "Crop Hybrid".
   */
  name: z.string(),
  /**
   * The operation's name for the application, e.g. "Planter". Absent when no included pass's
   * application list names it.
   */
  applicationName: z.string().optional(),
  units: z.string().optional(),
  legend: z.discriminatedUnion('kind', [
    z.object({
      kind: z.literal(LayerPaletteKind.Discrete),
      stops: z.array(reconciledStopSchema)
    }),
    z.object({
      kind: z.literal(LayerPaletteKind.Continuous),
      range: z.object({ minimum: z.number(), maximum: z.number() }),
      /**
       * One color per bin, lowest first: a bin below the range, the bins within it, and a bin
       * above it.
       */
      binColors: z.array(z.string()),
      /**
       * Whether the legend reads highest value first, as down force does so that its loss of
       * ground contact sits beside the lowest force.
       */
      descending: z.boolean(),
      scale: z.enum(LegendScaleKind),
      /**
       * The range and bin count the report would share across the layer's maps unless told
       * otherwise, for an editor to show and reset to.
       */
      defaults: z.object({
        range: z.object({ minimum: z.number(), maximum: z.number() }),
        binCount: z.int()
      }),
      /**
       * The widest range the layer's maps hold data across, within which an editor offers a range.
       */
      bounds: z.object({ minimum: z.number(), maximum: z.number() }),
      /**
       * Values the layer encodes outside its continuous scale, like down force's loss of ground
       * contact.
       */
      stops: z.array(reconciledStopSchema)
    })
  ])
})

const metricsBlockSchema = z.object({
  kind: z.literal(ReportBlockKind.Metrics),
  id: z.string(),
  title: z.string(),
  /**
   * Headline figures. A metric is present only when its underlying data is in scope, so an
   * absent metric means the question doesn't apply rather than that the answer was zero.
   */
  metrics: z.array(
    z.object({
      key: z.enum(ReportTotalMetric),
      label: z.string(),
      value: z.number(),
      unit: z.string().optional()
    })
  )
})

const activitySummaryBlockSchema = z.object({
  kind: z.literal(ReportBlockKind.ActivitySummary),
  id: z.string(),
  entries: z.array(
    z.object({
      task: taskSchema,
      acres: z.number(),
      fieldCount: z.number()
    })
  )
})

/**
 * One thing described by a handful of named values, rather than a set of things to compare. A table
 * with a single row states seven column headings to say seven numbers about one pass; the same
 * content as facts costs the labels and no more. Whether content is one or many is the report's to
 * say — the count of rows that happened to come back does not settle it.
 */
const factsBlockSchema = z.object({
  kind: z.literal(ReportBlockKind.Facts),
  id: z.string(),
  title: z.string(),
  icon: z.enum(SpriteMap).optional(),
  /**
   * The machine whose pass this content describes, when its group gathers more than one machine's
   * passes and the content stayed one machine's own.
   */
  serialNumber: z.number().optional(),
  items: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      /**
       * The value carries its own unit, as a cell does. A column can state a unit once in its header
       * because every row shares it; a single value has no column to put it above.
       */
      value: reportCellSchema
    })
  )
})

/** Content that stands on its own, as against a group that gathers some of it under a name. */
const leafBlockSchema = z.discriminatedUnion('kind', [
  tableBlockSchema,
  layerMapBlockSchema,
  unavailableMapBlockSchema,
  metricsBlockSchema,
  activitySummaryBlockSchema,
  factsBlockSchema
])

/**
 * Content under a name, for a record that holds more than one of something: a field's passes, each
 * with its own map and its own numbers. Without the name, a reader gets a map, some weather and a
 * summary three times over with nothing saying which belongs to which.
 *
 * Groups hold leaves only, the way a list cell holds scalars only — one level of gathering is what
 * the reports need, and a group inside a group has no reading.
 */
const groupBlockSchema = z.object({
  kind: z.literal(ReportBlockKind.Group),
  id: z.string(),
  /**
   * What was done and when. The group is named from the task rather than carrying a name of its own,
   * so the one enum settles its heading, its date and its colour, and none of the three can disagree
   * with the other two.
   */
  task: taskSchema,
  at: isoDatetimeToDate,
  /**
   * When the group gathers several passes, the time the last of them finished. `at` is then the time
   * the first did.
   */
  until: isoDatetimeToDate.optional(),
  blocks: z.array(leafBlockSchema)
})

const reportBlockSchema = z.discriminatedUnion('kind', [
  tableBlockSchema,
  layerMapBlockSchema,
  unavailableMapBlockSchema,
  metricsBlockSchema,
  activitySummaryBlockSchema,
  factsBlockSchema,
  groupBlockSchema
])

/** One step of where a record is. The entity is what to call the step, not how far down it sits. */
const addressSegmentSchema = z.object({
  entity: z.enum(ReportEntity),
  label: z.string()
})

/**
 * The thing a report is about, and the unit a reader files, prints and refers to — a field in one
 * report, a farm in another.
 *
 * Records are a flat list rather than a tree. Each carries its own address, so a record is legible on
 * its own with no page around it, and grouping is a matter of noticing that an address changed rather
 * than of nesting. Nothing depends on how deep a report's breakdown happens to run.
 */
const reportRecordSchema = z.object({
  /**
   * Stable across requests for the same content, so a reader's per-block state survives a
   * refetch and an anchor keeps pointing at what it named.
   */
  id: z.string(),
  entity: z.enum(ReportEntity),
  label: z.string(),
  /** Where this record is, outermost first. Empty when a report has nothing to locate it against. */
  address: z.array(addressSegmentSchema),
  blocks: z.array(reportBlockSchema)
})

export const reportDocumentSchema = z.object({
  meta: z.object({
    reportType: z.enum(ReportType),
    title: z.string(),
    season: z.number(),
    crop: cropSelectionSchema,
    generatedAt: isoDatetimeToDate,
    /**
     * Whether one record to a page is a sensible way to read this report. True of a report whose
     * records each hold a page of content, false of one whose record is a single table — a page per
     * farm holding one table of its fields is a stack of mostly blank paper.
     */
    paginatesByRecord: z.boolean().default(false)
  }),
  /** Report-wide content that belongs to no record, such as headline totals. */
  blocks: z.array(reportBlockSchema),
  records: z.array(reportRecordSchema),
  legends: z.array(layerLegendSchema).default([])
})

export namespace ReportDocument {
  export type Document = z.infer<typeof reportDocumentSchema>
  export type SerializedDocument = z.input<typeof reportDocumentSchema>
  export type Meta = Document['meta']
  export type Record = z.infer<typeof reportRecordSchema>
  export type AddressSegment = z.infer<typeof addressSegmentSchema>
  export type Block = z.infer<typeof reportBlockSchema>
  export type LeafBlock = z.infer<typeof leafBlockSchema>
  export type TableBlock = z.infer<typeof tableBlockSchema>
  export type LayerMapBlock = z.infer<typeof layerMapBlockSchema>
  export type LayerRender = z.infer<typeof layerRenderSchema>
  export type UnavailableMapBlock = z.infer<typeof unavailableMapBlockSchema>
  export type UnavailablePass = z.infer<typeof unavailablePassSchema>
  export type LayerLegend = z.infer<typeof layerLegendSchema>
  export type MetricsBlock = z.infer<typeof metricsBlockSchema>
  export type ActivitySummaryBlock = z.infer<typeof activitySummaryBlockSchema>
  export type FactsBlock = z.infer<typeof factsBlockSchema>
  export type GroupBlock = z.infer<typeof groupBlockSchema>
  export type Column = z.infer<typeof reportColumnSchema>
  export type Row = ReportRowNode
  export type Cell = z.infer<typeof reportCellSchema>
  export type ListItem = z.infer<typeof listItemSchema>
  export type MapLink = z.infer<typeof mapLinkSchema>
}

/**
 * Query options shared by the seasonal report APIs. `format` selects the response body: the
 * document described by {@link reportDocumentSchema} or a CSV file download.
 *
 * The two formats take different options, so they are separate members of a union rather than
 * one object with optional fields. CSV is formatted server-side and honors the reader's
 * `measureSystem`; a document always carries imperial values and units and leaves conversion to
 * the client, so a `measureSystem` on a document request would be silently ignored. Omitting
 * `format` selects the document.
 */
const reportQuerySchema = z.discriminatedUnion('format', [
  z.object({
    l: z.string().optional(),
    format: z.literal('csv'),
    measureSystem: z.enum(MeasureSystem).optional()
  }),
  z.object({
    l: z.string().optional(),
    format: z.literal('json').optional()
  })
])

/**
 * Serializes the query options into the shape `path` expects, narrowing on `format` so
 * `measureSystem` is only read from the branch that carries it.
 */
const reportQueryParams = (p: z.infer<typeof reportQuerySchema>) => ({
  l: p.l,
  format: p.format,
  measureSystem: p.format === 'csv' ? p.measureSystem : undefined
})

const reportPathSchema = z.object({
  orgCode: z.string(),
  season: z.coerce.number(),
  crop: cropSegmentCodec
})

export const seasonalInputReportApi = defineApi({
  pathSchema: reportPathSchema,
  querySchema: reportQuerySchema,
  pathFor: (p) =>
    path(
      `/tailor/${p.orgCode}/seasonal-report/${p.season}/${cropSegmentCodec.encode(p.crop)}/inputs`,
      reportQueryParams(p)
    ),
  name: 'GenerateSeasonalInputReport',
  description: 'Generate the seasonal input report of liquid, granular and hybrid products',
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  method: 'POST',
  requestBodySchema: selectedOptionsSchema,
  responseSchema: reportDocumentSchema,
  gatewayPath: '/:orgCode/seasonal-report/:season/:crop/inputs' as const,
  supportsCodec: true
})

export const condensedSeasonReportApi = defineApi({
  pathSchema: reportPathSchema,
  querySchema: reportQuerySchema,
  pathFor: (p) =>
    path(
      `/tailor/${p.orgCode}/seasonal-report/${p.season}/${cropSegmentCodec.encode(p.crop)}/condensed`,
      reportQueryParams(p)
    ),
  name: 'GenerateCondensedSeasonReport',
  description: 'Generate the condensed season report organized by client and farm',
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  method: 'POST',
  requestBodySchema: selectedOptionsSchema,
  responseSchema: reportDocumentSchema,
  gatewayPath: '/:orgCode/seasonal-report/:season/:crop/condensed' as const,
  supportsCodec: true
})

/**
 * One application's map layer, drawn for every pass that recorded it: the planter's hybrid map, say,
 * or the nitrogen system's rate. An application id is shared by every pass of the same application,
 * so its layer's maps are also the ones that share a legend.
 */
const reportLayerSchema = z.object({
  /**
   * The application's kind, which its id also implies. Carried so a layer's rows can be read by
   * prefix; a type that disagrees with the id reads nothing.
   */
  applicationType: applicationTypes,
  applicationId: z.string(),
  layerKey: z.string()
})

/**
 * The scale one application's continuous layer is drawn at. A layer with no setting shares the
 * scale the report reconciles for it, and a setting for a discrete layer changes nothing.
 */
const reportLegendSettingSchema = z.object({
  applicationType: applicationTypes,
  applicationId: z.string(),
  layerKey: z.string(),
  scale: legendScaleSchema
})

/**
 * The tasks whose passes over one field a report can read as one: two combines harvesting a field
 * between them, or two planters planting it.
 */
export const MERGEABLE_TASKS = [TaskType.Plant, TaskType.Harvest] as const

const agronomicReportOptionsSchema = selectedOptionsSchema.extend({
  /**
   * The map layers each pass shows while the report includes maps. An empty list shows each pass's
   * own default layer.
   */
  layers: z.array(reportLayerSchema).default([]),
  legends: z.array(reportLegendSettingSchema).default([]),
  /**
   * The tasks whose passes over each field are merged into one group, fusing what the passes laid
   * down or took off between them and keeping each machine's own readings apart.
   */
  mergedTasks: z.array(z.enum(MERGEABLE_TASKS)).default([])
})

export namespace AgronomicActivityReport {
  export type Options = z.infer<typeof agronomicReportOptionsSchema>
  export type Layer = z.infer<typeof reportLayerSchema>
  export type LegendSetting = z.infer<typeof reportLegendSettingSchema>
  export type LegendScale = z.infer<typeof legendScaleSchema>
  export type MergeableTask = (typeof MERGEABLE_TASKS)[number]
}

export const agronomicActivityReportApi = defineApi({
  pathSchema: reportPathSchema,
  querySchema: reportQuerySchema,
  pathFor: (p) =>
    path(
      `/tailor/${p.orgCode}/seasonal-report/${p.season}/${cropSegmentCodec.encode(p.crop)}/details`,
      reportQueryParams(p)
    ),
  name: 'GenerateAgronomicActivityReport',
  description: 'Generate the detailed agronomic activity report with maps and task-specific data',
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  method: 'POST',
  requestBodySchema: agronomicReportOptionsSchema,
  responseSchema: reportDocumentSchema,
  gatewayPath: '/:orgCode/seasonal-report/:season/:crop/details' as const,
  supportsCodec: true
})

/**
 * An application's map layer an agronomic activity report can draw for a season, named as the
 * passes that recorded it name the application and the layer.
 */
const reportLayerOptionSchema = reportLayerSchema.extend({
  /**
   * Absent when the pass that recorded the application had no application list stored.
   */
  applicationName: z.string().optional(),
  name: z.string()
})

export type ReportLayerOption = z.infer<typeof reportLayerOptionSchema>

export const reportLayerOptionsApi = defineApi({
  pathSchema: reportPathSchema,
  querySchema: z.object({ l: z.string().optional() }),
  pathFor: (p) =>
    path(
      `/tailor/${p.orgCode}/seasonal-report/${p.season}/${cropSegmentCodec.encode(p.crop)}/layers`,
      {
        l: p.l
      }
    ),
  name: 'GetReportLayerOptions',
  description:
    'List the map layers the passes of a season and crop recorded, each as an application (its type, id and name) and a layer (its key and name), for choosing which maps an agronomic activity report draws.',
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  method: 'GET',
  responseSchema: z.object({ items: z.array(reportLayerOptionSchema) }),
  gatewayPath: '/:orgCode/seasonal-report/:season/:crop/layers' as const
})
